// Lidkleuren (docs/DESIGN.md §3.2). Sleutels = packages/domain ColorKey (Okabe–Ito, #28).
// Componenten krijgen één set lokale custom properties (--mc-hue/-fill/-tint/-ink) zodat dezelfde
// CSS voor elke kleur en elk thema werkt.

export const MEMBER_COLOR_KEYS = [
  'orange',
  'skyblue',
  'bluishgreen',
  'yellow',
  'blue',
  'vermillion',
  'reddishpurple',
  'black',
] as const;
export type MemberColorKey = (typeof MEMBER_COLOR_KEYS)[number];

/** Aanbevolen volgorde voor een nieuw gezin: de zes best onderscheidbare tinten eerst. */
export const MEMBER_COLOR_DEFAULT_ORDER: ReadonlyArray<MemberColorKey> = [
  'blue',
  'orange',
  'bluishgreen',
  'reddishpurple',
  'skyblue',
  'vermillion',
  'yellow',
  'black',
];

/**
 * Neutraal grijs voor wat niet bij één lid hoort: het hele gezin, een mengsel van kleuren of een
 * onbekende sleutel. Los van de lidkleur 'black' (oprichter 1 okt: Baba's afspraken leken grijs).
 */
export const NEUTRAL_COLOR = 'neutral';

function known(key: string): string {
  return (MEMBER_COLOR_KEYS as ReadonlyArray<string>).indexOf(key) >= 0 ? key : NEUTRAL_COLOR;
}

/** Inline-style met de vier lidkleur-variabelen, bv. voor `style={memberStyle('orange')}`. */
export function memberStyle(colorKey: string | null | undefined): string {
  const k = known(colorKey || NEUTRAL_COLOR);
  return `--mc-hue:var(--m-${k});--mc-fill:var(--m-${k}-fill);--mc-tint:var(--m-${k}-tint);--mc-ink:var(--m-${k}-ink);--mc-wash:var(--m-${k}-wash)`;
}

/** Losse var()-verwijzingen voor één lidkleur. */
export function memberColor(colorKey: string | null | undefined): {
  hue: string;
  fill: string;
  tint: string;
  ink: string;
  /** UX-ronde bord 2: nog lichter dan tint (half paneel, half tint), voor rustige agendablokken. */
  wash: string;
} {
  const k = known(colorKey || NEUTRAL_COLOR);
  return {
    hue: `var(--m-${k})`,
    fill: `var(--m-${k}-fill)`,
    tint: `var(--m-${k}-tint)`,
    ink: `var(--m-${k}-ink)`,
    wash: `var(--m-${k}-wash)`,
  };
}

/** Sprint-1-export: Okabe–Ito-sleutel → volle tint (alleen voor kleine accenten). */
export const COLOR_VAR: Record<string, string> = {
  orange: 'var(--c-orange)',
  skyblue: 'var(--c-skyblue)',
  bluishgreen: 'var(--c-bluishgreen)',
  yellow: 'var(--c-yellow)',
  blue: 'var(--c-blue)',
  vermillion: 'var(--c-vermillion)',
  reddishpurple: 'var(--c-reddishpurple)',
  black: 'var(--c-black)',
};

/** Voegt klassenamen samen en laat lege weg (geen clsx-afhankelijkheid). */
export function cx(...parts: Array<string | false | null | undefined>): string {
  let out = '';
  for (const p of parts) if (p) out = out ? `${out} ${p}` : p;
  return out;
}
